// Plan 24c, Task 5 — Minigame HUD screen (Screen 22)
//
// Persistent HUD overlay rendered over the minigame arena.
// Does NOT intercept canvas input (pointer-events: none).
//
// Sections:
//   Top-left  — objective pill
//   Top-center — timer pill (MM:SS, danger color + pulse when ≤10s)
//   Top-right  — optional status badge
//   Left stack — optional per-player score rows
//   Bottom-center — optional controls hint pill (fades at t=3s)
//
// ESC key → return to warmup.
//
// Design system references:
//   docs/design/tokens.json
//   docs/design/screen-details/minigame-phase.md §Screen 22

import { SHAPE_GLYPH_FOR_COLOR, colorIdFromIndex } from './shape-palette';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { getInputModality } from '../../input/modality';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type MinigameHUDPayload = BoardMountPayload & {
  objectiveText: string;
  /** Count-down timer in seconds. Format as MM:SS. */
  timerSeconds?: number;
  /** Count-up elapsed in ms. Format as MM:SS of elapsed. */
  timerElapsedMs?: number;
  status?: { label: string; tone: 'success' | 'warn' | 'danger' };
  scores?: Array<{ playerId: string; score: number }>;
  showControlsHint?: boolean;
  /** Default adapts to the active input modality. */
  controlsHintText?: string;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** z-index.hud = 100 */
const Z_INDEX_HUD = 100;
/** color.brand.panel-bg-alpha */
const OVERLAY_BG = 'rgba(30, 35, 50, 0.82)';
/** border-radius.pill */
const PILL_RADIUS = '9999px';
/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";
/** typography.family.mono */
const MONO_FAMILY = "'Source Code Pro', 'Courier New', monospace";
/** typography.size.timer */
const TIMER_SIZE = '48px';
/** typography.size.h5 */
const H5_SIZE = '18px';
/** typography.size.body */
const BODY_SIZE = '16px';
/** typography.size.caption */
const CAPTION_SIZE = '14px';
/** color.text.primary */
const COLOR_WHITE = '#FFFFFF';
/** color.text.secondary */
const COLOR_TEXT_SECONDARY = '#B0B8C8';
/** color.text.muted */
const COLOR_TEXT_MUTED = '#707888';
/** color.text.danger */
const COLOR_TEXT_DANGER = '#E04040';
/** color.hud.alive-green */
const COLOR_ALIVE_GREEN = '#5CAD40';
/** color.brand.amber */
const COLOR_BRAND_AMBER = '#E8A020';
/** color.button.danger.base */
const COLOR_DANGER_BASE = '#D94040';
/** layout.hud-padding */
const HUD_PADDING = '12px';

/** Player color palette — index 0-9. Matches scoreboard.ts / round-summary.ts. */
const PLAYER_COLORS = [
  '#E04040', // 0 red
  '#4080D0', // 1 blue
  '#40B040', // 2 green
  '#E8D020', // 3 yellow
  '#E08820', // 4 orange
  '#9040C0', // 5 purple
  '#E06090', // 6 pink
  '#40B0C0', // 7 cyan
  '#E8E8E8', // 8 white
  '#404040', // 9 black
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a non-negative seconds count as MM:SS with leading zeros.
 */
function formatTime(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

/** Build the shared pill inline style string. */
function pillCss(extra: string[] = []): string {
  return [
    `background: ${OVERLAY_BG}`,
    `border-radius: ${PILL_RADIUS}`,
    'padding: 8px 16px',
    ...extra,
  ].join('; ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createMinigameHUDScreen(ctx: ScreenMountContext<MinigameHUDPayload>): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const {
    objectiveText,
    timerSeconds,
    timerElapsedMs,
    status,
    scores,
    showControlsHint,
    controlsHintText,
  } = payload;

  let disposed = false;

  // Collect all GSAP timeline handles for cleanup
  const gsapHandles: Array<{ dispose: () => void }> = [];

  // ── Root overlay ────────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-minigame-hud';
  root.setAttribute('data-screen-id', 'minigame-hud');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_HUD}`,
    'pointer-events: none',
  ].join('; ');

  // ── Top-left: objective pill ────────────────────────────────────────────────

  const objectivePill = document.createElement('div');
  objectivePill.className = 'minigame-hud__objective';
  objectivePill.style.cssText = [
    pillCss(),
    'position: absolute',
    `top: ${HUD_PADDING}`,
    `left: ${HUD_PADDING}`,
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H5_SIZE}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
  ].join('; ');
  objectivePill.textContent = objectiveText;
  root.appendChild(objectivePill);

  // ── Top-center: timer pill ──────────────────────────────────────────────────

  // Determine timer value
  const hasTimer = timerSeconds !== undefined || timerElapsedMs !== undefined;

  if (hasTimer) {
    let totalSecs: number;
    if (timerSeconds !== undefined) {
      totalSecs = timerSeconds;
    } else {
      totalSecs = (timerElapsedMs ?? 0) / 1000;
    }

    const timeText = formatTime(totalSecs);
    const isDanger = timerSeconds !== undefined && timerSeconds <= 10;

    const timerPill = document.createElement('div');
    timerPill.className = 'minigame-hud__timer';
    timerPill.style.cssText = [
      pillCss(),
      'position: absolute',
      `top: ${HUD_PADDING}`,
      'left: 50%',
      'transform: translateX(-50%)',
      `font-family: ${MONO_FAMILY}`,
      `font-size: ${TIMER_SIZE}`,
      'font-variant-numeric: tabular-nums',
      `color: ${isDanger ? COLOR_TEXT_DANGER : COLOR_WHITE}`,
    ].join('; ');
    timerPill.textContent = timeText;
    root.appendChild(timerPill);

    // Pulse animation when ≤10s countdown
    if (isDanger) {
      const pulseHandle = gsapRegistry.create('minigame-hud:timer-pulse');
      pulseHandle.timeline.to(timerPill, { scale: 1.06, repeat: -1, yoyo: true, duration: 0.5 });
      gsapHandles.push(pulseHandle);
    }
  }

  // ── Top-right: status badge ─────────────────────────────────────────────────

  if (status !== undefined) {
    const dotColor =
      status.tone === 'success'
        ? COLOR_ALIVE_GREEN
        : status.tone === 'warn'
          ? COLOR_BRAND_AMBER
          : COLOR_DANGER_BASE;

    const statusPill = document.createElement('div');
    statusPill.className = 'minigame-hud__status';
    statusPill.style.cssText = [
      pillCss(),
      'position: absolute',
      `top: ${HUD_PADDING}`,
      `right: ${HUD_PADDING}`,
      'display: flex',
      'align-items: center',
      'gap: 6px',
      `font-family: ${HEADLINE_FAMILY}`,
      `font-size: ${H5_SIZE}`,
      `color: ${COLOR_WHITE}`,
    ].join('; ');

    const dot = document.createElement('span');
    dot.className = 'minigame-hud__status-dot';
    dot.style.cssText = [
      'display: inline-block',
      'width: 10px',
      'height: 10px',
      `border-radius: ${PILL_RADIUS}`,
      `background-color: ${dotColor}`,
      'flex-shrink: 0',
    ].join('; ');
    statusPill.appendChild(dot);

    const labelSpan = document.createElement('span');
    labelSpan.textContent = status.label;
    statusPill.appendChild(labelSpan);

    root.appendChild(statusPill);
  }

  // ── Left stack: score rows ──────────────────────────────────────────────────

  if (scores !== undefined && scores.length > 0) {
    const scoreStack = document.createElement('div');
    scoreStack.className = 'minigame-hud__scores';
    scoreStack.style.cssText = [
      'position: absolute',
      'top: 56px',
      `left: ${HUD_PADDING}`,
      'display: flex',
      'flex-direction: column',
      'gap: 4px',
    ].join('; ');

    for (const entry of scores) {
      const player = payload.state.players.get(entry.playerId);
      const colorIndex = player != null ? player.color % 10 : -1;
      const dotColor = colorIndex >= 0 ? (PLAYER_COLORS[colorIndex] ?? '#808080') : '#808080';
      const colorId = colorIdFromIndex(colorIndex);
      const glyph = colorId != null ? (SHAPE_GLYPH_FOR_COLOR[colorId] ?? '') : '';

      const row = document.createElement('div');
      row.className = 'minigame-hud__score-row';
      row.style.cssText = [
        'display: flex',
        'align-items: center',
        'gap: 6px',
        `font-family: ${HEADLINE_FAMILY}`,
        `font-size: ${BODY_SIZE}`,
        `color: ${COLOR_WHITE}`,
      ].join('; ');

      const dot = document.createElement('span');
      dot.className = 'minigame-hud__score-dot';
      dot.style.cssText = [
        'display: inline-block',
        'width: 8px',
        'height: 8px',
        `border-radius: ${PILL_RADIUS}`,
        `background-color: ${dotColor}`,
        'flex-shrink: 0',
      ].join('; ');
      row.appendChild(dot);

      if (glyph) {
        const glyphSpan = document.createElement('span');
        glyphSpan.textContent = glyph;
        row.appendChild(glyphSpan);
      }

      const scoreSpan = document.createElement('span');
      scoreSpan.textContent = String(entry.score);
      row.appendChild(scoreSpan);

      scoreStack.appendChild(row);
    }

    root.appendChild(scoreStack);
  }

  // ── Bottom-center: controls hint ────────────────────────────────────────────

  if (showControlsHint) {
    const hintText =
      controlsHintText ??
      (getInputModality() === 'touch'
        ? 'Drag / Tap to Move | Tap Action'
        : 'WASD Move | Space Jump');

    const hintPill = document.createElement('div');
    hintPill.className = 'minigame-hud__controls-hint';
    hintPill.style.cssText = [
      pillCss(),
      'position: absolute',
      'bottom: 24px',
      'left: 50%',
      'transform: translateX(-50%)',
      `font-family: ${HEADLINE_FAMILY}`,
      `font-size: ${CAPTION_SIZE}`,
      `color: ${COLOR_TEXT_MUTED}`,
      'white-space: nowrap',
    ].join('; ');
    hintPill.textContent = hintText;
    root.appendChild(hintPill);

    const hintHandle = gsapRegistry.create('minigame-hud:hint-fade');
    hintHandle.timeline.to(hintPill, { opacity: 0, duration: 0.5 }, 3.0);
    gsapHandles.push(hintHandle);
  }

  // ── Append root to host ─────────────────────────────────────────────────────

  host.appendChild(root);

  // ── ESC key handler ─────────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ─────────────────────────────────────────────────────────────────

  return {
    element: root,
    dispose() {
      if (disposed) return;
      disposed = true;

      for (const handle of gsapHandles) {
        handle.dispose();
      }
      document.removeEventListener('keydown', handleEsc);
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
